<template>
  <footer class="bg-gray-800 text-white mt-12">
    <div class="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 text-sm">
        <div>
          <h3 class="text-base font-bold mb-2">
            <SiteBrandName v-if="showSiteBrandTitle" />
            <ManagerCompanyName v-else />
          </h3>
          <p v-if="displaySlogan" class="text-gray-400 text-xs leading-relaxed">
            {{ displaySlogan }}
          </p>
        </div>
        <div>
          <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-300 mb-2">{{ $t('footer.contact') }}</h4>
          <template v-if="hasContact">
            <p v-if="manager.email" class="text-gray-400 text-xs mb-1.5">
              <a :href="emailHref" class="hover:text-white transition">{{ manager.email }}</a>
            </p>
            <p v-if="manager.phone" class="text-gray-400 text-xs mb-2">
              <a :href="phoneHref" class="hover:text-white transition">{{ manager.phone }}</a>
            </p>
          </template>
          <p class="mt-2">
            <router-link
              :to="`/${currentLang}/feedback`"
              class="text-gray-400 hover:text-white transition text-xs"
            >
              {{ $t('feedback.title') }}
            </router-link>
          </p>
        </div>
        <div>
          <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-300 mb-2">{{ $t('footer.quickLinks') }}</h4>
          <ul class="space-y-1.5">
            <li>
              <router-link :to="`/${currentLang}/property`" class="text-gray-400 hover:text-white transition text-xs">
                {{ $t('nav.property') }}
              </router-link>
            </li>
            <li>
              <router-link :to="`/${currentLang}/location`" class="text-gray-400 hover:text-white transition text-xs">
                {{ $t('nav.location') }}
              </router-link>
            </li>
            <li>
              <router-link :to="`/${currentLang}/availability`" class="text-gray-400 hover:text-white transition text-xs">
                {{ $t('nav.availability') }}
              </router-link>
            </li>
          </ul>
        </div>
        <div>
          <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-300 mb-2">{{ $t('footer.legalLinks') }}</h4>
          <ul class="space-y-1.5">
            <li>
              <router-link :to="`/${currentLang}/privacy-policy`" class="text-gray-400 hover:text-white transition text-xs">
                {{ $t('footer.privacyPolicy') }}
              </router-link>
            </li>
            <li>
              <router-link :to="`/${currentLang}/terms-of-service`" class="text-gray-400 hover:text-white transition text-xs">
                {{ $t('footer.termsOfService') }}
              </router-link>
            </li>
          </ul>
        </div>
      </div>
      <div class="border-t border-gray-700 mt-5 pt-4 text-center text-gray-500 text-xs">
        <p>&copy; {{ copyrightYear }} {{ copyrightEntity }}. {{ $t('footer.rights') }}.</p>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { computed, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SiteBrandName from '../common/SiteBrandName.vue';
import ManagerCompanyName from '../common/ManagerCompanyName.vue';
import { usePropertyManagerContact } from '../../composables/usePropertyManagerContact.js';
import { useSiteBrand } from '../../composables/useSiteBrand.js';
import { useEffectiveLayout } from '../../composables/useEffectiveLayout.js';

const route = useRoute();
const { locale } = useI18n();
const effectiveLayout = useEffectiveLayout();
const showSiteBrandTitle = computed(() => effectiveLayout.value === 'public');
const { manager: contact, hasContact, emailHref, phoneHref } = usePropertyManagerContact();
const { siteName, siteSlogan } = useSiteBrand();

const manager = computed(() => contact.value || {});
const displaySlogan = computed(() => siteSlogan.value);
const copyrightYear = new Date().getFullYear();
const copyrightEntity = computed(() => {
  const company = (manager.value.companyName || '').trim();
  if (company) return company;
  return siteName.value || '';
});

const injectedLocale = inject('currentLocale', null);

const currentLang = computed(() => {
  if (injectedLocale) return injectedLocale;
  return route.params.lang || locale.value || 'fr';
});
</script>
